import type { AssessmentSessionAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import AssessmentQ from './AssessmentQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import SessionQ from './SessionQ'

const AssessmentSessionQ = {
  ...makeQueryModelV2<AssessmentSessionAttributes>('assessment_session')([
    'id',
    'assessment_id',
    'session_id',
    'released_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: { query: SessionQ, foreignKey: SessionQ.id, sourceKey: AssessmentSessionQ.session_id },
    Assessment: {
      query: AssessmentQ,
      foreignKey: AssessmentQ.id,
      sourceKey: AssessmentSessionQ.assessment_id,
    },
  }),

  where: (
    {
      id,
      sessionId,
      assessmentId,
      tutorId,
      released,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
    }: {
      id?: number | SQLFragment<number>
      sessionId?: number | SQLFragment<number>
      assessmentId?: number | SQLFragment<number>
      tutorId?: number | SQLFragment<number>
      released?: boolean
      include?: IncludeAssociations
      paranoid?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<AssessmentSessionAttributes> => SQL`
        FROM ${AssessmentSessionQ}
        ${includeAssociationsToSql(AssessmentSessionQ, include)}
        WHERE
            ${id != null ? SQL`${AssessmentSessionQ.id} = ${id} AND` : SQL``}
            ${sessionId != null ? SQL`${AssessmentSessionQ.session_id} = ${sessionId} AND` : SQL``}
            ${
              assessmentId != null
                ? SQL`${AssessmentSessionQ.assessment_id} = ${assessmentId} AND`
                : SQL``
            }
            ${
              released != null
                ? SQL`${AssessmentSessionQ.released_at} IS ${released ? SQL`NOT ` : SQL``} NULL ${
                    released ? SQL`AND` : SQL`OR`
                  } ${AssessmentSessionQ.released_at} ${released ? SQL`<=` : SQL`>`} NOW() AND `
                : SQL``
            }
            ${
              released != null
                ? SQL`${released ? SQL`NOT ` : SQL``}(${
                    AssessmentSessionQ.released_at
                  } IS NULL OR ${AssessmentSessionQ.released_at} > NOW()) AND `
                : SQL``
            }
            ${tutorId != null ? SQL`${SessionQ.user_id} = ${tutorId} AND` : SQL``}
            ${paranoid ? SQL`${AssessmentSessionQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  isAcceptingResponses: (): SQLFragment<boolean> =>
    SQL`${AssessmentSessionQ.released_at} IS NOT NULL`,
}

export default AssessmentSessionQ
